import type {ILocationToken} from "./location-compare-value-store-list";

/** Represents a group of location tokens for a single country with human-readable label. */
export interface ILocationLabelsGroup {
    countryLabel: string;
    tokens: ILocationToken[];
}

/**
 * Result structure for rendering location tags in UI.
 * 
 * Semantics:
 * - isSelectAll = true → render "All locations" tag
 * - isSelectAll = false, groups = [] → render nothing
 * - isSelectAll = false, groups = [data] → render country/province/city tags
 */
export interface ILocationLabelsGroups {
    isSelectAll: boolean;
    groups: Array<ILocationLabelsGroup>;
}

/**
 * LocationLabelsListStore (stateless utility) - groups location tokens by country for rendering tags.
 * Tokens received contain human-readable names, not codes.
 */
class LocationLabelsGrouper {

    /**
     * Groups tokens by country. Returns isSelectAll flag and grouped tokens.
     * undefined → no tags, [] → "All locations" tag, [tokens] → grouped tags.
     */
    public getGroupedLabels(tokens: ILocationToken[] | undefined): ILocationLabelsGroups {
        const isSelectAll = tokens?.length === 0;

        if (isSelectAll) {
            return {isSelectAll: true, groups: []};
        }

        if (!tokens?.length) {
            return {isSelectAll: false, groups: []};
        }

        const groupsMap = new Map<string, ILocationToken[]>();

        for (const token of tokens) {
            const countryLabel = token.country;

            if (!countryLabel) {
                continue;
            }

            if (!groupsMap.has(countryLabel)) {
                groupsMap.set(countryLabel, []);
            }

            groupsMap.get(countryLabel)!.push(token);
        }

        const groups = Array.from(groupsMap.entries())
            .map(([countryLabel, tokens]) => ({
                countryLabel,
                tokens
            }));

        return {isSelectAll: false, groups};
    }
}

export default LocationLabelsGrouper;
